import { useCallback, useEffect, useRef, useState } from 'react';

import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';

import type { FlyingAvatarRect } from '../components/FlyingAvatar';
import FlyingAvatar from '../components/FlyingAvatar';
import { useShortlistPreviewTarget } from '../ShortlistPreviewTargetContext';

type Flight = {
  imageUrl: string;
  sourceRect: FlyingAvatarRect;
  targetRect: FlyingAvatarRect;
};

type FlyToShortlistPreview = {
  /** Attach to the element the avatar takes off from. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onAdded: () => void;
  flyingAvatar: JSX.Element | null;
};

/** Sends a copy of the sitter's photo arcing from a search card to the contact list preview. */
const useFlyToShortlistPreview = (imageUrl: string | undefined): FlyToShortlistPreview => {
  const prefersReducedMotion = useMatchMedia('(prefers-reduced-motion: reduce)');
  const { getTargetRect } = useShortlistPreviewTarget();
  const anchorRef = useRef<HTMLElement>(null);
  const [flight, setFlight] = useState<Flight | null>(null);

  const frameRef = useRef(0);
  useEffect(
    () => () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }
    },
    []
  );

  const onAdded = useCallback(() => {
    const anchor = anchorRef.current;
    if (prefersReducedMotion || !anchor || !imageUrl) {
      return;
    }
    const sourceRect = anchor.getBoundingClientRect();
    // Adding the first sitter is what brings the preview on screen, and React has
    // not committed that render yet, so there is nothing to read until a frame later.
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = 0;
      const targetRect = getTargetRect();
      // The preview is suppressed. Better no animation than one towards a point
      // nobody can see.
      if (!targetRect) {
        return;
      }
      setFlight({ imageUrl, sourceRect, targetRect });
    });
  }, [prefersReducedMotion, imageUrl, getTargetRect]);

  return {
    anchorRef,
    onAdded,
    flyingAvatar: flight && <FlyingAvatar {...flight} onComplete={() => setFlight(null)} />,
  };
};

export default useFlyToShortlistPreview;
